import React from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import BusyTeammatesCard from "./BusyTeammatesCard";
import CallingHoursCard from "./CallingHoursCard";
import CallSummariesCard from "./CallSummariesCard";
import NumberValidationCard from "./NumberValidationCard";
import OptOutKeywordsCard from "./OptOutKeywordsCard";
import VoicemailDetectionCard from "./VoicemailDetectionCard";
import VoicemailTranscriptionCard from "./VoicemailTranscriptionCard";

const SETTINGS_VIEWS = [
  { value: "voicemail", label: "Voicemail" },
  { value: "calls", label: "Calls" },
  { value: "messaging", label: "Messaging" },
  { value: "compliance", label: "Compliance" },
] as const;
type SettingsView = (typeof SETTINGS_VIEWS)[number]["value"];

export default function TelephonySettingsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsParam = searchParams.get("settings");
  const activeView: SettingsView = SETTINGS_VIEWS.find((view) => view.value === settingsParam)?.value ?? "voicemail";

  const showView = (view: SettingsView) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("settings", view);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-1 rounded-lg border bg-card p-1">
        {SETTINGS_VIEWS.map((view) => {
          const isActive = view.value === activeView;
          return (
            <button
              key={view.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => showView(view.value)}
              className={cn(
                "rounded-md border px-3.5 py-1.5 text-sm font-medium",
                isActive
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-transparent text-muted-foreground hover:bg-muted",
              )}
            >
              {view.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        {activeView === "voicemail" && <VoicemailTranscriptionCard />}
        {activeView === "calls" && (
          <>
            <BusyTeammatesCard />
            <CallSummariesCard />
            <VoicemailDetectionCard />
          </>
        )}
        {activeView === "messaging" && <NumberValidationCard />}
        {activeView === "compliance" && (
          <>
            <CallingHoursCard />
            <OptOutKeywordsCard />
          </>
        )}
      </div>
    </div>
  );
}
